import ProjectSession from "../../../../../core/model2/project/project-session"
import MoveManager from "../../../../../core/gd/manager/move-manager"
import ArrayUtils from "../../../../../core/gd/utils/array"
import {DraggedOverPosition} from "../../../../../core/gd-modules/dnd/@types"
import {SpaceProto} from "../../../../../core/model2/space/space-proto"
import {BUS_TREE_CHANGE} from "../../../../../core/gd/bus/bus-event-type"
import NavigationMenu_ProjectsLocalSettings from "../../../local-settings"
import {menu_sortProjects} from "../../../menu-items/project/functions/order-projects"
import menu_reorderProjects from "../actions/reorder-projects"
import {MENU_PROJECTS_TREE_ROOT_ID} from "./@const"
import {MenuProjectDropResult} from "./@events"
import menuProjects_insertNextTo from "./insert-next-to"

export type MenuProjectsDropParams = {
    draggedProject  : ProjectSession
    space           : SpaceProto
    localSettings   : NavigationMenu_ProjectsLocalSettings
    dropResult      : MenuProjectDropResult
}

// const debug = "menuProjects_dropProject"
const debug = false

// 'root' is the sentinel projects-tree passes for the projects space - the API wants a real null there
function _toParentId(parentId: ID): ID | null {
    if (parentId === MENU_PROJECTS_TREE_ROOT_ID) {
        return null
    }

    return parentId
}

function _maxSortPosition(projects: ProjectSession[]): number {
    const positions = _.map(projects, (project: ProjectSession) => {
        return project.sortPosition || 0
    })

    return _.max(positions) || 0
}

// Move to a different parent. The tree is updated locally first - waiting for the API round trip
// would leave the row sitting under its old parent until the server pushes the change back.
function _reParent(params: MenuProjectsDropParams, newParentId: ID | null, ordered: ProjectSession[], sortPosition: number) {
    const draggedProject = params.draggedProject

    debug && console.log(debug, '_reParent', draggedProject.name, newParentId, sortPosition)

    // the server inserts at sortPosition and pushes the later siblings down - mirror it locally, or the
    // dropped row ties with its neighbour until the new positions come back
    _.forEach(ordered, (project: ProjectSession) => {
        if (
            project.id !== draggedProject.id
            && (project.sortPosition || 0) >= sortPosition
        ) {
            project.sortPosition = (project.sortPosition || 0) + 1
        }
    })

    draggedProject.parentId     = newParentId
    draggedProject.sortPosition = sortPosition

    // re-parenting needs full access, so sortPosition is saved - a sortedMap left behind would outrank it
    // (menu_sortProjects) and freeze this list, see menu_reorderProjects
    delete gd.user.menu.projectsAll.sortedMap[params.space.sortMapId]
    gd.user.menu.save()

    gd.session.projects.initTree()
    gd.bus.triggerEvent(BUS_TREE_CHANGE, {})

    MoveManager.moveAndSortProjectToProject(draggedProject.id, newParentId, sortPosition)
}

export default function menuProjects_dropProject(params: MenuProjectsDropParams) {
    const draggedProject    = params.draggedProject
    const target            = params.dropResult.targetProject

    debug && console.log(debug, 'drop', draggedProject.name, '->', target.name, params.dropResult)

    if (target.id === draggedProject.id) {
        return
    }

    if (params.dropResult.isDropInto) {
        // already a child of the target - nothing to move
        if (draggedProject.parentId === target.id) {
            return
        }

        const children  = menu_sortProjects(target.treeChildren, params.space)
        const ordered   = children.concat([draggedProject])

        // the moved row lands inside the target, so open it first or the row just disappears -
        // _reParent re-renders the tree synchronously and would read a stale collapsed state
        params.localSettings.expandedProjects = ArrayUtils.addValueToArray(params.localSettings.expandedProjects, target.id)

        _reParent(params, target.id, ordered, _maxSortPosition(children) + 1)

        return
    }

    const isHoverTop    = params.dropResult.type === DraggedOverPosition.TOP
    const siblings      = params.dropResult.targetSiblings || []
    const ordered       = menuProjects_insertNextTo(siblings, draggedProject, target, isHoverTop)

    if (draggedProject.parentId === _toParentId(params.dropResult.targetParentId!)) {
        // same parent - the existing reorder already handles the sort map and the sort positions
        menu_reorderProjects(params.space.sortMapId, params.dropResult.targetParentId!, {
            originalOrderedProjects : siblings,
            reorderedProjects       : ordered,
            projectMoved            : draggedProject,
        })

        gd.bus.triggerEvent(BUS_TREE_CHANGE, {})

        return
    }

    // dropped between two rows belonging to a different parent - reorder and re-parent at once
    _reParent(
        params,
        _toParentId(params.dropResult.targetParentId!),
        ordered,
        (target.sortPosition || 0) + (isHoverTop ? 0 : 1)
    )
}
